/**
 * "What's new" screen: who sees it, and what it shows.
 *
 * Shared spec with Swift and Android (same names, same semantics). Shown once
 * to existing users after an update; a fresh install never sees the release it
 * was installed on. The key survives the sign-out wipe (`wipeLocalStorage` in
 * `store/auth.ts`).
 *
 * The release and its slides are generated from changelog/ (`whatsNew.data.ts`,
 * `pnpm release gen`): the newest release with a highlight for the web. Every
 * highlight is its own illustrated page (a slide). A signed-in account that is
 * not on the newsletter gets one more page at the end, inviting it. Nothing
 * here changes between releases.
 */
import type { IconId } from '@caliu/styles/icons/data'
import type { AuthUser } from '../api/auth'
import { ONBOARDING_COMPLETED_KEY } from './onboarding'
import { WHATS_NEW_RELEASE, WHATS_NEW_SLIDES } from './whatsNew.data'

export { WHATS_NEW_RELEASE, WHATS_NEW_SLIDES }

/**
 * The last release id this browser was shown (or skipped). Web-only `-v2`: the
 * first 1.6.1 sheet could not be scrolled on phones, so the browsers it marked
 * as seen get the fixed one once. Native keeps `caliu-whats-new-seen`.
 */
export const WHATS_NEW_SEEN_KEY = 'caliu-whats-new-seen-v2'

/** Numeric dotted compare; missing or non-numeric parts count as 0. */
export function compareReleases(a: string, b: string): number {
  const pa = a.split('.')
  const pb = b.split('.')
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = Number.parseInt(pa[i] ?? '', 10) || 0
    const y = Number.parseInt(pb[i] ?? '', 10) || 0
    if (x !== y) return x < y ? -1 : 1
  }
  return 0
}

/**
 * Never seen a release: only an existing user (onboarding already completed
 * before this launch) gets it. Otherwise: anything older than `release`.
 */
export function shouldShowWhatsNew(
  lastSeen: string | null,
  isExistingUser: boolean,
  release: string,
): boolean {
  return lastSeen === null ? isExistingUser : compareReleases(lastSeen, release) < 0
}

/**
 * The illustrated pages the web can draw, one per highlight (every highlight
 * has one). A changelog highlight for the web that names any other slide fails
 * to typecheck in `whatsNew.data.ts`, which is the point.
 */
export type WhatsNewSlide = 'tagIcons' | 'newNoteStart' | 'typography'

/** One page of the screen: a highlight's slide, or the newsletter invite. */
export type WhatsNewPage = { kind: 'slide'; id: WhatsNewSlide } | { kind: 'newsletter'; id: 'newsletter' }

/**
 * Whether the screen ends with the newsletter invite: a signed-in account with
 * an address, whose newsletter state is known, available (not bounced) and off.
 * Decided when the screen opens, so turning it on there keeps the page.
 */
export function offersNewsletter(user: Pick<AuthUser, 'email' | 'newsletter'> | null | undefined): boolean {
  return !!user?.email && user.newsletter?.available === true && !user.newsletter.subscribed
}

/** The highlights' slides in release order, then the newsletter invite. */
export function whatsNewPages(
  slides: readonly WhatsNewSlide[] = WHATS_NEW_SLIDES,
  { newsletter = false }: { newsletter?: boolean } = {},
): WhatsNewPage[] {
  const pages: WhatsNewPage[] = slides.map((id) => ({ kind: 'slide', id }))
  if (newsletter) pages.push({ kind: 'newsletter', id: 'newsletter' })
  return pages
}

/** The tagIcons hero: 6 × 7 Espurna tag icons, A to Z. */
export const WHATS_NEW_TAG_ICONS: readonly IconId[] = [
  'atom', 'bicycle', 'bird', 'bot', 'brain', 'butterfly',
  'cactus', 'cake', 'chess', 'coffee', 'compass', 'cpu',
  'croissant', 'crown', 'dice', 'flame', 'flask', 'gamepad',
  'gem', 'guitar', 'headphones', 'lightbulb', 'mountain', 'mushroom',
  'palette', 'paw', 'pizza', 'plane', 'popcorn', 'puzzle',
  'rainbow', 'rocket', 'snowflake', 'sprout', 'telescope', 'tent',
  'terminal', 'trophy', 'umbrella', 'vinyl', 'wave', 'wine',
]

/** The one highlighted tile in the tagIcons hero. */
export const WHATS_NEW_HIGHLIGHTED_ICON: IconId = 'telescope'

type LaunchStorage = Pick<Storage, 'getItem' | 'setItem'>

/**
 * The launch decision: read the last seen release and the onboarding flag,
 * decide, then ALWAYS mark this release as seen (so a fresh install is marked
 * and a shown screen never comes back). Must run before anything in this launch
 * can complete onboarding; `whatsNewAtLaunch()` runs it once per page load.
 */
export function resolveWhatsNewAtLaunch(
  storage?: LaunchStorage,
  release: string | null = WHATS_NEW_RELEASE,
): boolean {
  // No release has ever had news for the web: nothing to show or mark.
  if (release === null) return false
  let show = false
  try {
    const store = storage ?? localStorage
    const lastSeen = store.getItem(WHATS_NEW_SEEN_KEY)
    const isExistingUser = store.getItem(ONBOARDING_COMPLETED_KEY) === 'true'
    show = shouldShowWhatsNew(lastSeen, isExistingUser, release)
    store.setItem(WHATS_NEW_SEEN_KEY, release)
  } catch {
    // Storage unavailable (private mode edge cases): an unwritable flag would
    // bring the screen back on every load, so don't show it at all.
    return false
  }
  return show
}

let launchDecision: boolean | null = null

/** `resolveWhatsNewAtLaunch()`, run once per page load (first call wins). */
export function whatsNewAtLaunch(): boolean {
  launchDecision ??= resolveWhatsNewAtLaunch()
  return launchDecision
}
